import { DISCOVERIES, totalDiscoverable, HINTS } from '../data/discoveries.js';

const HINTS_KEY = 'meghas-universe:hints';
const HINTED_KEY = 'meghas-universe:hinted-ids';
const MAX_HINTS = 3;

/**
 * HUD.js - manages the HTML overlay elements:
 *  - Discovery checklist (top-right)
 *  - Wish counter (hidden until wishes are wired)
 *  - Mute button
 *  - Drag hint
 */
export class HUD {
  constructor() {
    // Wish counter (hidden until wishes wired up)
    this.counter = document.getElementById('wish-counter');

    // Mute button - hidden for now (no audio in this version)
    this.muteButton = document.getElementById('mute-button');
    if (this.muteButton) this.muteButton.style.display = 'none';
    this.muted = false;

    // Drag hint
    this.hint = document.getElementById('drag-hint');

    // Black hole alert (bottom-center, orange, fired once when the BH appears)
    this.bhAlert = document.getElementById('bh-alert');

    // Discovery flash (bottom-center, gold, fired on each new discovery)
    this.discoveryFlash = document.getElementById('discovery-flash');

    // === Discovery checklist ===
    this.checklistToggle = document.getElementById('checklist-toggle');
    this.checklistPanel = document.getElementById('checklist-panel');
    this.checklistCount = document.querySelector('#checklist-toggle .checklist-count');
    this.checklistHeader = document.querySelector('#checklist-panel .checklist-header');
    this.checklistCategoriesEl = document.querySelector('#checklist-panel .checklist-categories');

    this.discoveries = new Set();
    this.caughtWishes = new Set();
    this.allWishes = [];
    this.caughtWhispers = new Set();
    this.allWhispers = [];
    this._onReset = null;
    this._onWishReread = null;
    this._onHintRequested = null;

    // Hint system - persisted in localStorage
    this.hintsUsed = 0;
    this.hintedIds = new Set();
    this._loadHints();

    this.whisperLabel = document.getElementById('whisper-label');

    // Fun facts panel
    this.funFactsBtn = document.getElementById('fun-facts-btn');
    this.funFactsPanel = document.getElementById('fun-facts-panel');
    this.funFactsClose = document.getElementById('fun-facts-close');

    if (this.funFactsBtn) {
      this.funFactsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleFunFacts();
      });
    }
    if (this.funFactsClose) {
      this.funFactsClose.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closeFunFacts();
      });
    }

    if (this.checklistToggle) {
      this.checklistToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleChecklist();
      });
    }

    // Click outside to close checklist or fun facts
    document.addEventListener('click', (e) => {
      if (
        this.checklistPanel
        && !this.checklistPanel.classList.contains('hidden')
        && !this.checklistPanel.contains(e.target)
        && !this.checklistToggle.contains(e.target)
      ) {
        this.checklistPanel.classList.add('hidden');
      }
      if (
        this.funFactsPanel
        && this.funFactsPanel.classList.contains('visible')
        && !this.funFactsPanel.contains(e.target)
        && !this.funFactsBtn.contains(e.target)
      ) {
        this.closeFunFacts();
      }
    });

    // ESC to close fun facts
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.funFactsPanel?.classList.contains('visible')) {
        this.closeFunFacts();
      }
    });

    this.renderChecklist();
  }

  // === Discovery checklist methods ===

  setDiscoveries(set) {
    this.discoveries = set;
    this.renderChecklist();
  }

  setCaughtWishes(set, allWishes) {
    this.caughtWishes = set;
    if (allWishes) this.allWishes = allWishes;
    this.renderChecklist();
  }

  setCaughtWhispers(set, allWhispers) {
    this.caughtWhispers = set;
    if (allWhispers) this.allWhispers = allWhispers;
    this.renderChecklist();
  }

  showWhisper(text) {
    if (!this.whisperLabel) return;
    this.whisperLabel.textContent = `"${text}"`;
    this.whisperLabel.classList.add('visible');
  }

  hideWhisper() {
    if (!this.whisperLabel) return;
    this.whisperLabel.classList.remove('visible');
  }

  // === Fun Facts panel ===

  toggleFunFacts() {
    if (!this.funFactsPanel) return;
    if (this.funFactsPanel.classList.contains('visible')) {
      this.closeFunFacts();
    } else {
      this.funFactsPanel.classList.remove('hidden');
      // Force reflow so the transition runs
      void this.funFactsPanel.offsetHeight;
      this.funFactsPanel.classList.add('visible');
    }
  }

  closeFunFacts() {
    if (!this.funFactsPanel) return;
    this.funFactsPanel.classList.remove('visible');
    // Re-add hidden after transition completes
    setTimeout(() => {
      if (!this.funFactsPanel.classList.contains('visible')) {
        this.funFactsPanel.classList.add('hidden');
      }
    }, 400);
  }

  /**
   * Show the one-time black hole alert. CSS handles the full fade
   * in / hold / fade out animation, so we just set the text and
   * add the class. Subsequent calls re-trigger the animation.
   */
  showBlackHoleAlert(text) {
    if (!this.bhAlert) return;
    this.bhAlert.textContent = text;
    // Force a reflow so the animation re-runs even if .show is already set
    this.bhAlert.classList.remove('show');
    void this.bhAlert.offsetHeight;
    this.bhAlert.classList.add('show');
  }

  hideBlackHoleAlert() {
    if (!this.bhAlert) return;
    this.bhAlert.classList.remove('show');
  }

  /** Brief "✦ new discovery" flash at the bottom of the screen. */
  showDiscoveryFlash() {
    if (!this.discoveryFlash) return;
    this.discoveryFlash.classList.remove('show');
    void this.discoveryFlash.offsetHeight;
    this.discoveryFlash.classList.add('show');
  }

  // === Hints ===

  onHintRequested(fn) {
    this._onHintRequested = fn;
  }

  _loadHints() {
    try {
      const used = localStorage.getItem(HINTS_KEY);
      if (used) this.hintsUsed = parseInt(used, 10) || 0;
      const hinted = localStorage.getItem(HINTED_KEY);
      if (hinted) this.hintedIds = new Set(JSON.parse(hinted));
    } catch (e) { /* ignore */ }
  }

  _saveHints() {
    try {
      localStorage.setItem(HINTS_KEY, String(this.hintsUsed));
      localStorage.setItem(HINTED_KEY, JSON.stringify([...this.hintedIds]));
    } catch (e) { /* ignore */ }
  }

  _resetHints() {
    this.hintsUsed = 0;
    this.hintedIds.clear();
    localStorage.removeItem(HINTS_KEY);
    localStorage.removeItem(HINTED_KEY);
  }

  /**
   * Pick a random undiscovered item, show its clue, and fire the
   * callback so Game can pulse the star red.
   */
  useHint() {
    if (this.hintsUsed >= MAX_HINTS) return;

    // Gather all undiscovered IDs
    const undiscovered = [];
    for (const cat of Object.values(DISCOVERIES)) {
      for (const item of cat.items) {
        if (!this.discoveries.has(item.id)) undiscovered.push(item.id);
      }
    }
    if (undiscovered.length === 0) return;

    // Pick a random one
    const id = undiscovered[Math.floor(Math.random() * undiscovered.length)];
    this.hintsUsed++;
    this.hintedIds.add(id);
    this._saveHints();

    // Show the clue text
    const clue = HINTS[id] || 'Something is hidden in the sky.';
    this.showHintClue(clue);

    // Fire callback to pulse the star
    if (this._onHintRequested) this._onHintRequested(id);

    // Re-render to update hint button + red dots
    this.renderChecklist();
  }

  showHintClue(text) {
    if (!this.discoveryFlash) return;
    this.discoveryFlash.textContent = text;
    this.discoveryFlash.classList.remove('show', 'hint-show');
    this.discoveryFlash.style.color = 'rgba(255, 64, 96, 0.9)';
    void this.discoveryFlash.offsetHeight;
    this.discoveryFlash.classList.add('hint-show');
    // Reset after animation completes
    setTimeout(() => {
      this.discoveryFlash.style.color = '';
      this.discoveryFlash.textContent = '✦ new discovery';
      this.discoveryFlash.classList.remove('hint-show');
    }, 10500);
  }

  /** Wire a callback to be called when the user clicks the reset link. */
  onResetRequested(fn) {
    this._onReset = fn;
  }

  /** Wire a callback to be called when a caught wish is clicked to re-read. */
  onWishReread(fn) {
    this._onWishReread = fn;
  }

  toggleChecklist() {
    if (!this.checklistPanel) return;
    this.checklistPanel.classList.toggle('hidden');
  }

  renderChecklist() {
    if (!this.checklistCategoriesEl) return;

    const total = totalDiscoverable();
    // Count discoveries that are in the registry (filter out unknown ids)
    let found = 0;
    for (const cat of Object.values(DISCOVERIES)) {
      for (const item of cat.items) {
        if (this.discoveries.has(item.id)) found++;
      }
    }

    const allFound = found === total;
    if (this.checklistCount) this.checklistCount.textContent = `${found} / ${total}`;
    if (this.checklistHeader) {
      this.checklistHeader.textContent = allFound
        ? 'All discovered. But the sky still has secrets - hover around.'
        : `${found} / ${total} discovered`;
    }

    // Glow the toggle button when all discoveries are found
    if (this.checklistToggle) {
      if (allFound) {
        this.checklistToggle.classList.add('all-found');
      } else {
        this.checklistToggle.classList.remove('all-found');
      }
    }

    // Build category sections
    this.checklistCategoriesEl.innerHTML = '';

    for (const [key, category] of Object.entries(DISCOVERIES)) {
      const categoryEl = document.createElement('div');
      categoryEl.className = 'checklist-category';

      const foundItems = category.items.filter((item) => this.discoveries.has(item.id));
      const remaining = category.items.length - foundItems.length;

      // Category label with count
      const labelEl = document.createElement('div');
      labelEl.className = 'checklist-category-label';
      labelEl.textContent = `${category.label} - ${foundItems.length} / ${category.items.length}`;
      categoryEl.appendChild(labelEl);

      // Found items by name
      for (const item of foundItems) {
        const itemEl = document.createElement('div');
        itemEl.className = 'checklist-item';
        itemEl.innerHTML = `<span class="checklist-checkmark">✦</span><span class="checklist-name">${escapeHtml(item.label)}</span>`;
        categoryEl.appendChild(itemEl);
      }

      // Show hinted (but not yet found) items with a red dot
      for (const item of category.items) {
        if (!this.discoveries.has(item.id) && this.hintedIds.has(item.id)) {
          const hintEl = document.createElement('div');
          hintEl.className = 'checklist-item hinted';
          hintEl.innerHTML = `<span class="checklist-hint-dot">●</span><span class="checklist-name">${escapeHtml(item.label)}</span>`;
          categoryEl.appendChild(hintEl);
        }
      }

      // Placeholder for undiscovered items (exclude hinted ones from count)
      const hintedInCategory = category.items.filter(
        (item) => !this.discoveries.has(item.id) && this.hintedIds.has(item.id)
      ).length;
      const unhinted = remaining - hintedInCategory;
      if (unhinted > 0) {
        const moreEl = document.createElement('div');
        moreEl.className = 'checklist-more';
        if (key === 'hidden') {
          moreEl.textContent = `??? × ${unhinted}`;
        } else {
          moreEl.textContent = `+ ${unhinted} more to find`;
        }
        categoryEl.appendChild(moreEl);
      }

      this.checklistCategoriesEl.appendChild(categoryEl);
    }

    // === Wishboard section (separate from discoveries) ===
    if (this.allWishes && this.allWishes.length > 0) {
      const wishboardEl = document.createElement('div');
      wishboardEl.className = 'checklist-category checklist-wishboard';

      const wishCount = this.caughtWishes.size;
      const wishTotal = this.allWishes.length;

      const labelEl = document.createElement('div');
      labelEl.className = 'checklist-category-label';
      labelEl.textContent = `Wishes - ${wishCount} / ${wishTotal}`;
      wishboardEl.appendChild(labelEl);

      // Caught wishes - clickable to re-read
      for (const wish of this.allWishes) {
        if (!this.caughtWishes.has(wish.id)) continue;
        const wishEl = document.createElement('button');
        wishEl.type = 'button';
        wishEl.className = 'checklist-item checklist-wish-item found';
        wishEl.innerHTML = `<span class="checklist-checkmark">✦</span><span class="checklist-name">${escapeHtml(wish.name)}</span>`;
        wishEl.addEventListener('click', (e) => {
          e.stopPropagation();
          if (this._onWishReread) this._onWishReread(wish.id);
        });
        wishboardEl.appendChild(wishEl);
      }

      const remaining = wishTotal - wishCount;
      if (remaining > 0) {
        const moreEl = document.createElement('div');
        moreEl.className = 'checklist-more';
        moreEl.textContent = `+ ${remaining} more to catch`;
        wishboardEl.appendChild(moreEl);
      }

      this.checklistCategoriesEl.appendChild(wishboardEl);
    }

    // === Whispers section ===
    if (this.allWhispers && this.allWhispers.length > 0) {
      const whispersEl = document.createElement('div');
      whispersEl.className = 'checklist-category checklist-whispers';

      const wCount = this.caughtWhispers.size;
      const wTotal = this.allWhispers.length;

      const whisperLabel = document.createElement('div');
      whisperLabel.className = 'checklist-category-label';
      whisperLabel.textContent = `Whispers - ${wCount} / ${wTotal}`;
      whispersEl.appendChild(whisperLabel);

      // List discovered whispers as italic lines (no re-read needed,
      // they're tiny and the panel itself is the memory book)
      for (const w of this.allWhispers) {
        if (!this.caughtWhispers.has(w.id)) continue;
        const item = document.createElement('div');
        item.className = 'checklist-whisper-item';
        item.textContent = `"${w.text}"`;
        whispersEl.appendChild(item);
      }

      const whispersRemaining = wTotal - wCount;
      if (whispersRemaining > 0) {
        const moreEl = document.createElement('div');
        moreEl.className = 'checklist-more';
        moreEl.textContent = `+ ${whispersRemaining} more to find`;
        whispersEl.appendChild(moreEl);
      }

      this.checklistCategoriesEl.appendChild(whispersEl);
    }

    // Reset link at the bottom (so the visitor can experience the discovery joy fresh)
    const resetEl = document.createElement('button');
    // Hint button
    const hintsRemaining = MAX_HINTS - this.hintsUsed;
    if (hintsRemaining > 0 && !allFound) {
      const hintEl = document.createElement('button');
      hintEl.className = 'checklist-hint';
      hintEl.type = 'button';
      hintEl.textContent = `use hint (${hintsRemaining} remaining)`;
      hintEl.addEventListener('click', (e) => {
        e.stopPropagation();
        this.useHint();
      });
      this.checklistCategoriesEl.appendChild(hintEl);
    }

    // Reset button
    resetEl.className = 'checklist-reset';
    resetEl.type = 'button';
    resetEl.textContent = 'reset progress';
    resetEl.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this._onReset) this._onReset();
    });
    this.checklistCategoriesEl.appendChild(resetEl);
  }

  // === Mute (kept for future audio support) ===

  toggleMute() {
    this.muted = !this.muted;
    if (this.muteButton) {
      this.muteButton.textContent = this.muted ? '⌀' : '♪';
    }
  }

  // === Wish counter (kept for future use) ===

  setWishes(caught, total) {
    if (!this.counter) return;
    this.counter.textContent = `${caught} / ${total} wishes caught`;
    this.counter.classList.add('flash');
    setTimeout(() => this.counter?.classList.remove('flash'), 600);
  }
}

function escapeHtml(s) {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
